import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, KeyRound, ShieldCheck, LogOut } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { api, getHomeRouteByRole } from "@/lib/api";
import { toast } from "sonner";

function scorePassword(pw) {
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[a-z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return Math.min(score, 5);
}

const STRENGTH = [
  { label: "Muito fraca", cls: "bg-red-500", textCls: "text-red-700" },
  { label: "Fraca", cls: "bg-red-400", textCls: "text-red-700" },
  { label: "Razoável", cls: "bg-amber-400", textCls: "text-amber-800" },
  { label: "Boa", cls: "bg-amber-500", textCls: "text-amber-700" },
  { label: "Forte", cls: "bg-emerald-500", textCls: "text-emerald-700" },
  { label: "Excelente", cls: "bg-emerald-600", textCls: "text-emerald-700" },
];

export default function ChangePassword() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!user) {
    navigate("/login", { replace: true });
    return null;
  }

  const score = scorePassword(next);
  const strength = STRENGTH[score];
  const passwordsMatch = next === confirm && next.length > 0;
  const isStrongEnough = next.length >= 8 && score >= 3;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!current || !next || !confirm) {
      toast.error("Preencha todos os campos.");
      return;
    }
    if (next !== confirm) {
      toast.error("As senhas não conferem.");
      return;
    }
    if (next.length < 8) {
      toast.error("A nova senha deve ter pelo menos 8 caracteres.");
      return;
    }
    if (next === current) {
      toast.error("A nova senha precisa ser diferente da atual.");
      return;
    }
    setSaving(true);
    try {
      const { data: updated } = await api.post("/users/me/change-password", {
        current_password: current,
        new_password: next,
      });
      // Update localStorage user
      localStorage.setItem("ceap_user", JSON.stringify(updated));
      toast.success("Senha atualizada com sucesso.");
      // Force fresh me() to keep auth context in sync, then redirect
      window.location.href = getHomeRouteByRole(updated.role);
    } catch (err) {
      const msg = err.response?.data?.detail || "Erro ao trocar a senha.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="flex justify-center mb-6">
          <Logo height={64} />
        </div>

        <Card className="card-soft" data-testid="change-password-card">
          <CardContent className="p-8">
            <div className="flex items-start gap-3 mb-6">
              <div className="p-2 rounded-md bg-midnight/10 flex-shrink-0">
                <KeyRound className="w-5 h-5 text-midnight" />
              </div>
              <div>
                <h1 className="serif text-2xl font-semibold text-slate-900">
                  Defina sua senha
                </h1>
                <p className="text-sm text-slate-600 mt-1">
                  Olá, <strong>{user.name}</strong>. Por segurança, defina uma nova senha
                  para continuar usando a plataforma.
                </p>
              </div>
            </div>

            <div className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-900">
              <strong>Requisitos:</strong> mínimo 8 caracteres, com letras maiúsculas, minúsculas e números.
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="current" className="text-xs font-medium text-slate-700">
                  Senha atual
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="current"
                    type={showCurrent ? "text" : "password"}
                    value={current}
                    onChange={(e) => setCurrent(e.target.value)}
                    required
                    className="h-11 pr-10"
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrent((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                    aria-label={showCurrent ? "Ocultar" : "Mostrar"}
                  >
                    {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="new" className="text-xs font-medium text-slate-700">
                  Nova senha
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="new"
                    type={showNext ? "text" : "password"}
                    value={next}
                    onChange={(e) => setNext(e.target.value)}
                    required
                    className="h-11 pr-10"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNext((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                    aria-label={showNext ? "Ocultar" : "Mostrar"}
                  >
                    {showNext ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {next.length > 0 && (
                  <div className="mt-2">
                    <div className="flex gap-1 mb-1">
                      {[...Array(5)].map((_, i) => (
                        <div
                          key={i}
                          className={`h-1.5 flex-1 rounded-full ${
                            i < score ? strength.cls : "bg-slate-200"
                          }`}
                        />
                      ))}
                    </div>
                    <div className={`text-xs ${strength.textCls}`}>
                      Força: <strong>{strength.label}</strong>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="confirm" className="text-xs font-medium text-slate-700">
                  Confirme a nova senha
                </Label>
                <Input
                  id="confirm"
                  type={showNext ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  className="mt-1 h-11"
                  data-testid="confirm-password-input"
                />
                {confirm.length > 0 && !passwordsMatch && (
                  <div className="text-xs text-red-600 mt-1">
                    As senhas não conferem.
                  </div>
                )}
                {passwordsMatch && isStrongEnough && (
                  <div className="text-xs text-emerald-700 mt-1 inline-flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" /> Tudo certo!
                  </div>
                )}
              </div>

              <Button
                type="submit"
                disabled={saving || !passwordsMatch || !isStrongEnough}
                className="w-full h-11 bg-midnight hover:bg-ink text-white font-medium disabled:opacity-50"
                data-testid="submit-new-password"
              >
                {saving ? "Salvando..." : "Salvar nova senha"}
              </Button>

              <button
                type="button"
                onClick={handleLogout}
                className="w-full text-center text-xs text-slate-500 hover:text-slate-700 inline-flex items-center justify-center gap-1 mt-2"
                data-testid="logout-from-change-password"
              >
                <LogOut className="w-3 h-3" /> Sair sem alterar
              </button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
